// ════════════════════════════════════════════════════════════
//  online/bets.js — Mini Dust on a fight, player against player
// ════════════════════════════════════════════════════════════
//  caP'n: "playing or watching, doesn't matter, you and a friend can
//  bet on the game with the in game currency (Mini Dust)".
//
//  ── THE SHAPE: a POOL per fight, no house ───────────────────
//  Everyone who bets puts their stake on Blue (0) or Red (1). When the
//  fight ends, the winning side's bettors split the losing side's
//  stakes in proportion to what they put in, and get their own stake
//  back. Nobody (no house, no Pier) takes a cut — NO FEE, ANYWHERE.
//    · nobody backed the winner → everyone is refunded
//    · nobody backed the loser  → winners just get their stake back
//    · the fight is void        → everyone is refunded
//  Mini Dust is never minted or burned by betting, only moved — so
//  betting can never become a faucet (THE FAUCET LAW's spirit).
//
//  ── WHO WRITES WHAT ─────────────────────────────────────────
//  You only ever write YOUR OWN wallet (profiles/{uid} is owner-sealed).
//  Placing a bet: take the stake (delta), then record it. Collecting:
//  YOUR screen works out your share from the public pool and the
//  result, claims it once (a transaction), then pays you. So a payout
//  never needs anyone else to be online — you collect next time you
//  open Shatterproof (the index under players/{uid}/bets remembers).
//
//  ⚠ TESTING-PHASE TRUST: the pool lives under games/shatterproof (the
//  open umbrella), so a hand-edited client could lie about a bet. Fine
//  while it's caP'n and friends; priced stakes between strangers wait
//  on server authority (ie-functions), like every other till.
// ════════════════════════════════════════════════════════════
import { BASE, deltaDust } from "./ie.js";
import { settle } from "./bets-math.js";
import { BET_MIN, BET_MAX } from "./dials.js";

export { BET_MIN, BET_MAX } from "./dials.js";

const idxPath = (uid) => `${BASE}/players/${uid}/bets`;
const keyOf = (poolPath) => poolPath.replace(/[.#$[\]/]/g, "~");

export { settle, poolTotals } from "./bets-math.js";

/** Place a bet. One per player per fight. Resolves { ok, reason } — never throws. */
export async function placeBet(net, { poolPath, uid, name, side, amount, lockAt, now, keys = null, fighters = [] }) {
  amount = Math.floor(Number(amount));
  if (!uid) return { ok: false, reason: "Sign in to bet." };
  if (fighters.includes(uid)) return { ok: false, reason: "You can't bet on a fight you're in." };
  if (!(side === 0 || side === 1)) return { ok: false, reason: "Pick a side." };
  if (!(amount >= BET_MIN && amount <= BET_MAX)) return { ok: false, reason: `Bets are ${BET_MIN}–${BET_MAX.toLocaleString()} Mini Dust.` };
  if (lockAt != null && now() >= lockAt) return { ok: false, reason: "Betting closed when the fight started." };
  try {
    const mine = await net.get(`${poolPath}/${uid}`);
    if (mine) return { ok: false, reason: "You already bet on this one." };
    // the index FIRST: if anything below fails, collect() still finds this pool and
    // settles it (a bet that was never recorded just settles to nothing owed)
    await net.set(`${idxPath(uid)}/${keyOf(poolPath)}`, { pool: poolPath, side, amount });
  } catch { return { ok: false, reason: "Couldn't reach the Empire. Try again." }; }
  // take the stake before recording it: a bet that's recorded but never paid for is free money
  const after = await deltaDust(uid, -amount).catch(() => null);
  if (after == null) { await net.remove(`${idxPath(uid)}/${keyOf(poolPath)}`).catch(() => {}); return { ok: false, reason: "Not enough Mini Dust." }; }
  let rec = null;
  try { rec = await net.txn(`${poolPath}/${uid}`, (cur) => (cur ? undefined : { side, amount, name: name || "Someone", at: net.TS(), ...(keys ? { keys } : {}) })); }
  catch { rec = null; }
  if (!rec || !rec.committed) {
    await deltaDust(uid, amount).catch(() => {});   // refund the stake
    return { ok: false, reason: rec ? "You already bet on this one." : "Couldn't place it — your stake is back." };
  }
  return { ok: true };
}

/**
 * Collect everything I'm owed. Call on boot and every so often.
 * resultOf(poolPath) → Promise<{ winner, void, lockAt, keys } | null> (null = not decided yet).
 * Resolves [{ pool, amount, stake, won }] for what was paid this call.
 */
export async function collect(net, uid, resultOf, now = Date.now) {
  if (!uid) return [];
  const idx = (await net.get(idxPath(uid))) || {};
  const paid = [];
  for (const [k, entry] of Object.entries(idx)) {
    const res = await resultOf(entry.pool).catch(() => null);
    if (!res) continue;
    const bets = (await net.get(entry.pool)) || {};
    const mine = bets[uid];
    if (!mine || mine.paid) { await net.remove(`${idxPath(uid)}/${k}`).catch(() => {}); continue; }
    const owed = settle(bets, res.winner, { lockAt: res.lockAt ?? Infinity, void: !!res.void, keys: res.keys || null })[uid] || 0;
    // CLAIM, PAY, MARK PAID. The claim is a transaction so two tabs can't both pay.
    // A claim left unpaid for a minute is a screen that crashed mid-collect: it is
    // re-claimed — again by transaction, matching the old claim exactly — and paid.
    const stamp = net.TS();
    const claim = await net.txn(`${entry.pool}/${uid}/claimed`, (cur) => {
      if (cur == null) return { owed, at: stamp };
      if (typeof cur.at === "number" && now() - cur.at > 60000 && !mine.paid) return { owed, at: stamp, retry: (cur.retry || 0) + 1 };
      return undefined;
    }).catch(() => null);
    if (!claim || !claim.committed) continue;
    const latest = await net.get(`${entry.pool}/${uid}/paid`).catch(() => null);
    if (latest) continue;
    if (owed > 0) { const after = await deltaDust(uid, owed); if (after == null) continue; }
    await net.set(`${entry.pool}/${uid}/paid`, true).catch(() => {});
    await net.remove(`${idxPath(uid)}/${k}`).catch(() => {});
    paid.push({ pool: entry.pool, amount: owed, stake: mine.amount, side: mine.side, won: res.void ? null : mine.side === res.winner });
  }
  return paid;
}
